import { Button, EmptyState, cn } from '@chopchop-se/ui';
import type { Notification } from '@warehouse/shared';
import { BellOff, Check } from 'lucide-react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { formatDateTime } from '../../../lib/format';
import { notificationIcon, notificationLink, notificationText } from './notification-text';

/**
 * The notification rows themselves — shared by the bell dropdown and the
 * full-page centre so both render the same sentence for the same event.
 */
export function NotificationList({
  notifications,
  portal,
  onMarkRead,
  onNavigate,
  compact = false,
}: {
  notifications: readonly Notification[];
  portal: boolean;
  onMarkRead: (id: string) => void;
  onNavigate?: () => void;
  compact?: boolean;
}): ReactNode {
  const { t } = useTranslation(['claims', 'common']);

  if (notifications.length === 0) {
    return (
      <EmptyState
        bare
        icon={<BellOff />}
        title={t('claims:notifications.empty')}
        description={compact ? undefined : t('claims:notifications.emptyDescription')}
      />
    );
  }

  return (
    <ul className="divide-y divide-surface-border" data-testid="notification-list">
      {notifications.map((notification) => {
        const Icon = notificationIcon(notification);
        const text = notificationText(notification, t);
        const to = notificationLink(notification, portal);
        const unread = notification.readAt === null;

        const body = (
          <span className="flex min-w-0 flex-1 flex-col">
            <span className={cn('truncate text-sm', unread ? 'font-medium text-ink' : 'text-ink-light')}>
              {text}
            </span>
            <span className="text-xs text-ink-muted">{formatDateTime(notification.createdAt)}</span>
          </span>
        );

        return (
          <li key={notification.id} className={cn('flex items-start gap-3 px-3 py-2.5', unread && 'bg-brand-600/[0.04]')}>
            <Icon className={cn('mt-0.5 size-4 shrink-0', unread ? 'text-brand-600' : 'text-ink-muted')} aria-hidden />
            {to ? (
              <Link to={to} className="flex min-w-0 flex-1" onClick={onNavigate}>
                {body}
              </Link>
            ) : (
              body
            )}
            {unread ? (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                aria-label={`${t('claims:notifications.markRead')}: ${text}`}
                title={t('claims:notifications.markRead')}
                icon={<Check />}
                onClick={() => {
                  onMarkRead(notification.id);
                }}
              />
            ) : null}
          </li>
        );
      })}
    </ul>
  );
}
